<template>
    <!-- 
        多點觸控優化樣式：
        - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
        - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
        - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
        - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
    -->
    <div class="molegame-container" style="touch-action: none; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
        <!-- 游戏区域 -->
        <div class="game-area" ref="gameAreaRef"
            :style="{ 'background': mockImg, 'background-size': 'cover', 'background-repeat': 'no-repeat', 'background-position': 'center bottom' }">

            <div class="game-header">
                <div class="game-topic">{{ gameConfig.topic }}</div>
                <div v-if="isPreviewMode" class="preview-badge">{{ $t('gameActManager.previewMode') }}</div>
            </div>
            <div class="timer">{{ formatTime(timeRemaining) }}
                <button class="start-button" @click="toggleGame">
                    <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
                </button>
            </div>

            <!-- 遊戲開始前彈窗區 -->
            <div v-if="beforeGame" class="before-game-container">
                <!-- 正式活動模式：左右佈局（單層） -->
                <div v-if="isFromActId" class="actid-mode-wrapper">
                    <div class="actid-mode-modal">
                        <!-- 左側：遊戲資訊和播放按鈕 -->
                        <div class="game-info-left">
                            <div class="game-title">{{ gameConfig.topic }}</div>
                            <div class="game-info">
                                <div class="game-time">{{ $t('Molegame.gameDuration') }}： {{ gameConfig.gameDuration }} {{
                        $t('Molegame.second') }}</div>
                                <div class="game-time">{{ $t('Molegame.moleSpeed') }}： {{ (gameConfig.moleSpeed / 1000).toFixed(1) }} {{
                        $t('Molegame.second') }}</div>
                            </div>
                            <!-- 按鈕區 -->
                            <div class="left-buttons">
                                <button class="action-button play-button play-in-left" @click="showReadyGoCountdown()">
                                    <svg-icon icon-class="play" class="play-icon" />
                                </button>
                            </div>
                        </div>

                        <!-- 右側：學生資訊輸入 -->
                        <div class="student-info-right">
                            <h4 class="student-info-title">{{ $t('gameActManager.enterStudentInfo') }}</h4>

                            <!-- HiTeach 名單下拉選單模式 -->
                            <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                                <div class="form-group">
                                    <select
                                        @change="handleRosterSelection"
                                        class="student-input student-select"
                                    >
                                        <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                                        <option
                                            v-for="member in classRoster"
                                            :key="`${member.memberName}-${member.seatID}`"
                                            :value="`${member.memberName}-${member.seatID}`"
                                        >
                                            {{ member.seatID }} - {{ member.memberName }}
                                        </option>
                                    </select>
                                    <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                </div>

                                <!-- 顯示已選擇的資訊（唯讀） -->
                                <div v-if="studentInfo.name" class="selected-info">
                                    <div class="info-item">
                                        <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                                        <span class="info-value">{{ studentInfo.seatNumber }}</span>
                                    </div>
                                     <div class="info-item">
                                        <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                                        <span class="info-value">{{ studentInfo.name }}</span>
                                    </div>
                                </div>
                            </template>

                            <!-- 手動輸入模式（預設） -->
                            <template v-else>
                                <div class="form-group">
                                    <input
                                        v-model="studentInfo.seatNumber"
                                        type="text"
                                        :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                                        maxlength="3"
                                        class="student-input"
                                    />
                                    <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
                                </div>
                                <div class="form-group">
                                    <input
                                        v-model="studentInfo.name"
                                        type="text"
                                        :placeholder="$t('gameActManager.studentNamePlaceholder')"
                                        maxlength="20"
                                        class="student-input"
                                    />
                                    <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- 切換遊戲模板區塊（僅在允許切換時顯示） -->
                    <div v-if="allowTemplateChange" class="game-template-switcher">
                        <div class="switcher-title">{{ $t('gameActManager.switchGameTemplate') }}</div>
                        <div class="switcher-content">
                            <select v-model="selectedGameTemplate" class="template-select">
                                <option value="mole">{{ $t('gameActManager.moleGame') }}</option>
                                <option value="star">{{ $t('gameActManager.starGame') }}</option>
                                <option value="snake">{{ $t('gameActManager.snakeGame') }}</option>
                            </select>
                            <button class="goto-button" @click="navigateToSelectedGame" :disabled="selectedGameTemplate === 'mole'">
                                <svg-icon icon-class="arrowright" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Demo/WebView 模式：原有佈局 -->
                <div v-else class="before-game-modal">
                    <div class="game-title">{{ gameConfig.topic }}</div>
                    <div class="game-info">
                        <div class="game-time">{{ $t('Molegame.gameDuration') }}： {{ gameConfig.gameDuration }} {{
                $t('Molegame.second') }}</div>
                        <div class="game-time">{{ $t('Molegame.moleSpeed') }}： {{ (gameConfig.moleSpeed / 1000).toFixed(1) }} {{
                $t('Molegame.second') }}</div>
                    </div>

                    <!-- 按鈕區 -->
                    <div class="game-buttons">
                        <button class="action-button play-button" @click="showReadyGoCountdown()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button v-if="!hasConfigUrl && !isPreviewMode" class="action-button setting-button" @click="goToSettings()">
                            <svg-icon icon-class="setting-game" class="setting-icon" />
                        </button>
                        <button v-if="isWebView && !isPreviewMode && isLoggedIn" class="action-button push-button"
                                :class="{ 'disabled': isSendDisabled }"
                                @click="handleSendClick()">
                            <svg-icon icon-class="upload" class="sendweb-icon" />
                        </button>
                        <!-- ✨ 新增：查看成績按鈕（只在 WebView 且已推送時顯示） -->
                        <button v-if="isWebView && !isPreviewMode && pushedActivityId"
                                class="action-button view-scores-button"
                                @click="openScoresPanel"
                                :title="$t('HTwbActKitHome.viewScores') || '查看成績'">
                            <svg-icon icon-class="clipboard-list" class="scores-icon" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Ready, Go! 提示 -->
            <div v-if="showReadyGo" class="ready-go-container">
                <div class="ready-go-text">{{ readyGoText }}</div>
            </div>

            <!---遊戲主畫面-->
            <!-- 打地鼠遊戲區域 -->
            <div class="mole-game-container">
                <div class="score-display">
                    <div class="score-value">{{ score }}</div>
                </div>
                <!-- 半圓形草地背景 -->
                <div class="ground-background" :class="{
                    'desert-ground': gameConfig.background === 'desert',
                    'forest-ground': gameConfig.background === 'forest'
                }"></div>
                <div class="mole-holes-container">
                    <div class="mole-grid">
                        <div v-for="(hole, index) in moleHoles" :key="index" class="mole-hole">
                            <div class="mole-hole-bg"></div>
                            <div class="mole" 
                                :class="{ 'active': hole.active, 'hit': hole.hit }" 
                                :style="{ 'background-image': hole.hit ? 'url(' + require('@/assets/HTwbActAssets/MolegameObj/mole-hit.webp') + ')' : 'url(' + require('@/assets/HTwbActAssets/MolegameObj/mole.webp') + ')' }"
                                @click="hitMole(index)"
                                @touchstart="handleTouchStart($event, index)"
                                @touchend="handleTouchEnd($event)">
                                <div v-if="hole.active && !hole.hit" class="mole-text" :class="{ 'correct': hole.isCorrect, 'wrong': !hole.isCorrect }">
                                    {{ hole.text }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- 飛行分數動畫 -->
                <transition-group name="flying-score" tag="div">
                    <div
                        v-for="effect in flyingScoreEffects"
                        :key="effect.id"
                        class="flying-score-effect"
                        :style="{
                            left: effect.currentX + 'px',
                            top: effect.currentY + 'px',
                            color: effect.isCorrect ? 'blue' : 'red',
                            opacity: effect.opacity
                        }"
                    >
                        {{ effect.text }}
                    </div>
                </transition-group>
            </div>

            <!-- 遊戲暫停彈窗 -->
            <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
                <div class="pause-game-modal">
                    <div class="pause-title">{{ $t('Molegame.gamePaused') || '活動暫停' }}</div>
                    <div class="pause-buttons">
                        <button class="action-button continue-button" @click="resumeGame()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button class="action-button restart-button" @click="resetGame()">
                            <svg-icon icon-class="refresh" class="refresh-icon" />
                        </button>
                    </div>
                </div>
            </div>
            <!-- 遊戲結束彈窗 -->
            <div v-if="gameOver" class="game-over-modal">
                <div class="mole-game-result-container" v-show="!showAnswerModal">
                    <!-- 結果卡片(顯示分數,正確錯誤次數) -->
                    <div class="result-card-wrap">
                        <div class="result-card">
                            <div class="result-header">
                                <div class="mole-result-title">{{ $t('Molegame.gameOver') || '遊戲結束' }}</div>
                            </div>
                            <div class="result-score">
                                <div class="star-score">
                                    <span class="score-number">{{ score }}</span>
                                </div>
                            </div>
                            <div class="result-details">
                                <div class="detail-item">
                                    {{ $t('Molegame.correctItems') }}: {{ correctCount }} {{ $t('Molegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Molegame.errorItems') }}: {{ wrongCount }} {{ $t('Molegame.items') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- 答案彈窗 -->
                <div v-if="showAnswerModal" class="answer-modal-mask">
                    <div class="answer-modal-card">
                        <div class="answer-modal-header">
                            <span class="answer-modal-title">{{ $t('Molegame.referenceAnswers') }}</span>
                            <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label correct">✔</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.correctVocabularyList" :key="'c-' + idx"
                                    class="answer-item correct">{{ ans }}</span>
                            </div>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label wrong">✘</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.errorVocabularyList" :key="'e-' + idx"
                                    class="answer-item wrong">{{ ans }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 底部按鈕區 -->
                <div class="game-result-actions" v-show="!showAnswerModal">
                    <button class="action-button answer-button" @click="toggleAnswerModal">
                        <span class="answer-text">{{ $t('Molegame.showAnswers') }}</span>
                    </button>
                    <button class="action-button restart-button" @click="resetGame">
                        <svg-icon icon-class="refresh" class="refresh-icon" />
                    </button>
                </div>
            </div>
        </div>

        <!-- ✨ 新增：成績查看面板 -->
        <ActivityScoresPanel
            v-if="showScoresPanel"
            :activity-id="pushedActivityId"
            :activity-name="pushedActivityName"
            :roster-source="classRosterSource || 'general'"
            @back="closeScoresPanel"
        />

        <!-- 推送確認彈窗 -->
        <PushConfirmModal
            v-model="showPushConfirm"
            :activity-name="gameConfig.topic"
            @confirm="executePush"
        />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, inject, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';
import ActivityScoresPanel from '@/GameActManager/components/ActivityScoresPanel.vue';
import PushConfirmModal from '@/GameActManager/components/PushConfirmModal.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';
import { validateAndRedirectGame } from '@/utils/gameActivityRouter';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { useGameActivityPush } from '@/composable/useGameActivityPush';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';

const router = useRouter();
const { t } = useI18n();
const iviewMessage = inject("$Message");

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTMoleGamePlay');

// 使用遊戲訊息橋接器 Composable（處理 Init.GameHome 和使用者驗證）
useGameMessageBridge('HTMoleGamePlay');

// 預覽模式相關
const isPreviewMode = ref(false);

// 正式活動模式相關
const isFromActId = ref(false);

// 遊戲模板切換相關
const hasGameSwitched = ref(false); // 是否已經切換過遊戲
const allowTemplateChange = ref(true); // 活動是否允許切換遊戲模板（從 API 讀取）
const selectedGameTemplate = ref('mole'); // 當前選擇的遊戲模板（預設為打地鼠）

// HiTeach 名單相關
const classRosterSource = ref(''); // 名單來源
const classRoster = ref([]); // 解析後的名單陣列
const useHiTeachRoster = ref(false); // 是否使用 HiTeach 名單

const studentInfo = reactive({
  name: '',
  seatNumber: ''
});
const studentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器（直接傳遞 callback，不包裝，以便移除）
    addEventListener(callback) {
        const wrappedCallback = (event) => callback(event);
        // 將包裝後的回調存儲起來，以便後續移除
        if (!this._listeners) {
            this._listeners = new WeakMap();
        }
        this._listeners.set(callback, wrappedCallback);

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', wrappedCallback);
        } else {
            window.addEventListener('message', wrappedCallback);
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (!this._listeners) {
            return;
        }
        const wrappedCallback = this._listeners.get(callback);
        if (!wrappedCallback) {
            return;
        }

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', wrappedCallback);
        } else {
            window.removeEventListener('message', wrappedCallback);
        }
        this._listeners.delete(callback);
    }
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    if (isSendDisabled.value) {
        iviewMessage.warning({
            content: t('Molegame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }

    showPushConfirm.value = true;
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isSendDisabled = ref(false);
const cooldownTimer = ref(null);

// ✨ 成績查看面板相關狀態
const showScoresPanel = ref(false);
const pushedActivityId = ref('');  // 儲存最新推送的活動 ID
const pushedActivityName = ref(''); // 儲存活動名稱

// 推送確認彈窗狀態
const showPushConfirm = ref(false);

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 短網址域名配置（已移至 useGameActivityPush composable 中）
// const shortURLHost = computed(() => {
//     return locale.value === 'zh-CN' ? 'hiteach.cn' : 'hiteach.cc';
// });

// 使用統一的遊戲活動推送 composable
const { createAndPushShortUrl, isLoggedIn } = useGameActivityPush({
    gameName: 'mole',
    gameType: 'molegame',
    gameRoute: '/ht-molegame-play',
    questionType: 'recognition',
    messageBridge: messageBridge,
    htVersion: htVersion,
    serializeGameConfig: (config) => JSON.stringify(config)
});

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

// 遊戲配置
let gameConfig = reactive({
    topic: 'empty state',
    correctVocabularyList: [],
    errorVocabularyList: [],
    gameDuration: 0,
    background: '',
    moleSpeed: 2500
});

// 遊戲區域大小
const gameAreaRef = ref(null);
const gameAreaWidth = ref(0);
const gameAreaHeight = ref(0);

// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const beforeGame = ref(true);
const showAnswerModal = ref(false);
const scoreSubmitted = ref(false); // 防抖旗標：確保成績只提交一次

// 計時相關
const timeRemaining = ref(0);
const timerInterval = ref(null);

// 分數顯示相關
const score = ref(0);
const correctCount = ref(0);
const wrongCount = ref(0);
const flyingScoreEffects = ref([]);
let flyingScoreId = 0;

// Ready, Go! 倒數相關
const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

// 地鼠洞
const moleHoles = ref([]);
const molePopInterval = ref(null);
// 地鼠相關狀態

// 音效相關
const readySound = ref(null);
const goSound = ref(null);
const backgroundMusic = ref(null);
const hitSound = ref(null);
const wrongHitSound = ref(null); // 打到錯誤答案的音效
const gameOverSound = ref(null);
const isMuted = ref(false);

// 隨機背景
const mockImg = computed(() => {
    const backgrounds = {
        forest: require('@/assets/HTwbActAssets/fantasy_forest.webp'),
        desert: require('@/assets/HTwbActAssets/desert_oasis.webp')
    };
    
    const defaultBg = require('@/assets/HTwbActAssets/fantasy_forest.webp');
    
    if (!gameConfig.background) return `url(${defaultBg})`;
    
    const bgPath = backgrounds[gameConfig.background] || defaultBg;
    return `url(${bgPath})`;
});

// 初始化地鼠洞
const initMoleHoles = () => {
    // 建立 3x3 的地鼠洞陣列（九宮格）
    moleHoles.value = Array(9).fill().map(() => ({
        active: false,
        hit: false,
        text: '',
        isCorrect: false
    }));
};

// 讓地鼠隨機彈出 - 支援多人遊戲，可同時彈出多隻地鼠
const popRandomMole = () => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    // 取得可用的洞
    const availableHoles = moleHoles.value
        .map((hole, index) => ({ hole, index }))
        .filter(item => !item.hole.active && !item.hole.hit);
    
    if (availableHoles.length === 0) return;
    
    // 決定這次要彈出幾隻地鼠 (1-3隻，根據可用洞數調整)
    const maxMoles = Math.min(3, availableHoles.length);
    const moleCount = Math.floor(Math.random() * maxMoles) + 1;
    
    // 隨機選擇要彈出地鼠的洞
    const selectedHoles = [];
    const availableIndices = [...availableHoles];
    
    for (let i = 0; i < moleCount; i++) {
        if (availableIndices.length === 0) break;
        
        const randomIndex = Math.floor(Math.random() * availableIndices.length);
        const selectedHole = availableIndices.splice(randomIndex, 1)[0];
        selectedHoles.push(selectedHole);
    }
    
    // 為每個選中的洞設定地鼠
    selectedHoles.forEach(({ index: holeIndex }) => {
        // 決定是正確還是錯誤詞彙
        const isCorrect = Math.random() < 0.6; // 60% 機率是正確詞彙
        
        const wordPool = isCorrect ? gameConfig.correctVocabularyList : gameConfig.errorVocabularyList;
        
        if (!wordPool || wordPool.length === 0) return;
        
        const randomWord = wordPool[Math.floor(Math.random() * wordPool.length)];
        
        // 設定地鼠狀態
        moleHoles.value[holeIndex] = {
            active: true,
            hit: false,
            text: randomWord,
            isCorrect: isCorrect
        };
        
        // 設定地鼠自動縮回（根據配置的速度調整停留時間）
        const baseHideTime = gameConfig.moleSpeed || 2500;
        const hideTime = baseHideTime + Math.random() * 1000; // 基礎時間 + 隨機0-1秒
        setTimeout(() => {
            if (moleHoles.value[holeIndex] && moleHoles.value[holeIndex].active && !moleHoles.value[holeIndex].hit) {
                moleHoles.value[holeIndex].active = false;
            }
        }, hideTime);
    });
};

// 觸控處理標記，避免重複觸發
const processedTouches = ref(new Set());

// 簡化的觸控開始處理 - 立即觸發，支援多點觸控
const handleTouchStart = (event, index) => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    // 遍歷所有新的觸控點
    for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        const touchId = touch.identifier;
        
        // 防止重複處理同一個觸控點
        if (!processedTouches.value.has(touchId)) {
            processedTouches.value.add(touchId);
            
            // 立即觸發打擊，不等 touchend
            hitMole(index);
            
            // 延遲清除觸控標記
            setTimeout(() => {
                processedTouches.value.delete(touchId);
            }, 100);
        }
    }
    
    // 阻止預設行為，避免觸發 click 事件
    event.preventDefault();
    event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
    // 清理結束的觸控點標記
    for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        processedTouches.value.delete(touch.identifier);
    }
};

// 點擊地鼠 - 支援多人同時點擊不同地鼠
const hitMole = (index) => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    const hole = moleHoles.value[index];
    
    // 原子性檢查並立即標記，防止多點觸控重複處理
    if (!hole.active || hole.hit) return;
    hole.hit = true; // 立即標記為已被打中，防止重複點擊
    
    // 取得遊戲區域
    const gameArea = gameAreaRef.value;
    const gameRect = gameArea.getBoundingClientRect();
    
    // 取得地鼠位置 (大約估算)
    const holeWidth = gameRect.width / 3;
    const holeHeight = gameRect.height / 3;
    const row = Math.floor(index / 3);
    const col = index % 3;
    
    const itemX = col * holeWidth + holeWidth / 2 + gameRect.left;
    const itemY = row * holeHeight + holeHeight / 2 + gameRect.top;
    
    // 決定分數
    let effectText = '';
    let isCorrect = false;
    
    if (hole.isCorrect) {
        effectText = '+10';
        isCorrect = true;
        score.value += 10;
        correctCount.value++;
    } else {
        effectText = '-5';
        isCorrect = false;
        score.value = Math.max(0, score.value - 5); // 防止分數為負
        wrongCount.value++;
    }
    
    // 正確答案播放打擊音效
    if (hole.isCorrect) {
        playSound(hitSound.value);
    } else {
        // 錯誤答案播放尖叫音效
        playSound(wrongHitSound.value);
    }
    
    // 新增飛行分數效果 - 固定在點擊位置附近
    const id = flyingScoreId++;
    const effect = {
        id,
        text: effectText,
        isCorrect,
        currentX: itemX,
        currentY: itemY - 30, // 顯示在點擊位置上方一點
        opacity: 1
    };
    flyingScoreEffects.value.push(effect);
    
    // 簡化動畫，只做淡出效果，不移動位置
    const duration = 1000;
    setTimeout(() => {
        // 淡出效果
        const fadeOutDuration = 300;
        let opacity = 1;
        const fadeInterval = setInterval(() => {
            opacity -= 0.1;
            if (opacity <= 0) {
                clearInterval(fadeInterval);
                // 移除效果
                flyingScoreEffects.value = flyingScoreEffects.value.filter(e => e.id !== id);
            } else {
                // 更新透明度
                const effectIndex = flyingScoreEffects.value.findIndex(e => e.id === id);
                if (effectIndex !== -1) {
                    flyingScoreEffects.value[effectIndex].opacity = opacity;
                }
            }
        }, fadeOutDuration / 10);
    }, duration - 300); // 顯示一段時間後開始淡出
    
    // 一段時間後讓地鼠縮回
    setTimeout(() => {
        hole.active = false;
        hole.hit = false;
    }, 300); // 縮短時間讓地鼠更快消失，提升遊戲節奏
    
    // 播放打擊音效
    playSound(hitSound.value);
};

// 格式化时间显示
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
    if (!gameStarted.value) {
        // 如果遊戲還沒開始，則顯示Ready, Go!倒數
        showReadyGoCountdown();
    } else if (!gamePaused.value) {
        // 如果遊戲正在進行，則暫停
        pauseGame();
    } else {
        // 如果遊戲已暫停，則繼續
        resumeGame();
    }
};

// 顯示Ready, Go!倒數效果
const showReadyGoCountdown = () => {
    // 正式活動模式：驗證學生資訊
    if (isFromActId.value) {
      // 清空錯誤訊息
      studentInfoErrors.name = '';
      studentInfoErrors.seatNumber = '';

      // 驗證姓名
      if (!studentInfo.name.trim()) {
        studentInfoErrors.name = t('gameActManager.studentNameRequired');
        return;
      }
      if (studentInfo.name.length > 20) {
        studentInfoErrors.name = t('gameActManager.studentNameTooLong');
        return;
      }

      // 驗證座號（選填，但如果填寫必須符合格式）
      if (studentInfo.seatNumber && !/^\d{1,3}$/.test(studentInfo.seatNumber)) {
        studentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        return;
      }

      // 儲存到 sessionStorage
      sessionStorage.setItem('studentInfo', JSON.stringify({
        name: studentInfo.name.trim(),
        seatNumber: studentInfo.seatNumber.trim()
      }));
    }

    // 停止並重置所有音效
    if (readySound.value) {
      readySound.value.pause();
      readySound.value.currentTime = 0;
    }
    if (goSound.value) {
      goSound.value.pause();
      goSound.value.currentTime = 0;
    }
    if (backgroundMusic.value) {
      backgroundMusic.value.pause();
      backgroundMusic.value.currentTime = 0;
    }
    if (hitSound.value) {
      hitSound.value.pause();
      hitSound.value.currentTime = 0;
    }
    if (wrongHitSound.value) {
      wrongHitSound.value.pause();
      wrongHitSound.value.currentTime = 0;
    }
    if (gameOverSound.value) {
      gameOverSound.value.pause();
      gameOverSound.value.currentTime = 0;
    }

    if (isCountingDown.value) return;

    isCountingDown.value = true;
    showReadyGo.value = true;
    beforeGame.value = false;
    
    // 顯示Ready...
    readyGoText.value = 'Ready...';
    // 播放Ready音效
    playSound(readySound.value);
    
    // 2秒後顯示Go!
    setTimeout(() => {
        readyGoText.value = 'Go!';
        // 播放Go音效
        playSound(goSound.value);
        
        // 1秒後開始遊戲
        setTimeout(() => {
            showReadyGo.value = false;
            isCountingDown.value = false;
            startGame();
        }, 1000);
    }, 2000);
};

// 開始遊戲
const startGame = () => {
    // 發送遊戲開始協議給 WebView
    messageBridge.postMessage({
        action: "Game.Start",
        type: "mole"
    });

    // 初始化遊戲
    gameStarted.value = true;
    gamePaused.value = false;
    gameOver.value = false;
    beforeGame.value = false;

    // 重設分數
    score.value = 0;
    correctCount.value = 0;
    wrongCount.value = 0;
    
    // 初始化地鼠洞
    initMoleHoles();
    
    // 播放背景音樂
    playSound(backgroundMusic.value);
    
    // 設定計時器
    timeRemaining.value = gameConfig.gameDuration;
    clearInterval(timerInterval.value);
    timerInterval.value = setInterval(() => {
        if (timeRemaining.value > 0) {
            timeRemaining.value--;
        } else {
            endGame();
        }
    }, 1000);
    
    // 設定地鼠彈出定時器
    clearInterval(molePopInterval.value);
    molePopInterval.value = setInterval(() => {
        popRandomMole();
    }, gameConfig.moleSpeed || 1000); // 使用配置的地鼠速度
};

// 暫停遊戲
const pauseGame = () => {
    if (!gameStarted.value || gameOver.value) return;
    
    gamePaused.value = true;
    
    // 暫停計時器
    clearInterval(timerInterval.value);
    
    // 暫停地鼠彈出
    clearInterval(molePopInterval.value);
    
    // 暫停背景音樂
    if (backgroundMusic.value) {
        backgroundMusic.value.pause();
    }
};

// 繼續遊戲
const resumeGame = () => {
    if (!gameStarted.value || gameOver.value || !gamePaused.value) return;
    
    gamePaused.value = false;
    
    // 繼續背景音樂
    if (backgroundMusic.value && !isMuted.value) {
        backgroundMusic.value.play().catch(error => console.error('繼續播放背景音樂失敗:', error));
    }
    
    // 繼續計時器
    timerInterval.value = setInterval(() => {
        if (timeRemaining.value > 0) {
            timeRemaining.value--;
        } else {
            endGame();
        }
    }, 1000);
    
    // 繼續地鼠彈出 - 使用配置的地鼠速度
    molePopInterval.value = setInterval(() => {
        popRandomMole();
    }, gameConfig.moleSpeed || 1000);
};

// 結束遊戲
const endGame = async () => {
    gameStarted.value = false;
    gameOver.value = true;

    // 停止計時器
    clearInterval(timerInterval.value);

    // 停止地鼠彈出
    clearInterval(molePopInterval.value);

    // 停止背景音樂
    stopSound(backgroundMusic.value);

    // 播放遊戲結束音效
    playSound(gameOverSound.value);

    // 隱藏所有地鼠
    moleHoles.value.forEach(hole => {
        hole.active = false;
        hole.hit = false;
    });

    // 🆕 提交成績（僅限正式活動模式且未切換遊戲，且只提交一次）
    if (isFromActId.value && !hasGameSwitched.value && !scoreSubmitted.value) {
        await submitGameScore();
    } else if (isFromActId.value && hasGameSwitched.value) {
        // 切換模板後不提交成績，顯示提示訊息
        iviewMessage.warning({
            content: t('gameActManager.templateSwitchedNoScore'),
            duration: 5,
            closable: true
        });
        console.log('⚠️ 因切換遊戲模板，不提交成績');
    }
};

// 🆕 提交遊戲成績到伺服器
const submitGameScore = async () => {
    // 防抖檢查：如果已經提交過，直接返回
    if (scoreSubmitted.value) {
        console.log('⚠️ 成績已經提交過，跳過重複提交');
        return;
    }

    // 設置旗標，防止重複提交
    scoreSubmitted.value = true;

    try {
        // 從 sessionStorage 取得學生資訊
        const studentData = JSON.parse(sessionStorage.getItem('studentInfo') || '{}');

        // 從 URL 取得 actId
        const urlParams = new URLSearchParams(window.location.search);
        const actId = urlParams.get('actId');

        if (!actId) {
            console.error('無法提交成績：缺少 actId');
            scoreSubmitted.value = false; // 如果失敗，重置旗標允許重試
            return;
        }

        if (!studentData.name) {
            console.error('無法提交成績：缺少學生姓名');
            scoreSubmitted.value = false; // 如果失敗，重置旗標允許重試
            return;
        }

        // 準備成績資料
        const scoreData = {
            activityId: actId,
            studentName: studentData.name,
            seatNumber: studentData.seatNumber || '',
            score: score.value,
            correctCount: correctCount.value,
            wrongCount: wrongCount.value
        };

        console.log('📤 提交成績資料:', scoreData);

        // 呼叫 API 提交成績
        const response = await gameActivityAPI.submitScore(scoreData);

        if (response.status === 200) {
            console.log('✅ 成績提交成功:', response.data);
        }
    } catch (error) {
        console.error('❌ 提交成績失敗:', error);
    }
};

// 重置遊戲
const resetGame = () => {
    gameStarted.value = false;
    gamePaused.value = false;
    gameOver.value = false;
    beforeGame.value = true;
    showAnswerModal.value = false;
    scoreSubmitted.value = false; // 重置成績提交旗標，允許下次遊戲提交

    // 清除計時器
    clearInterval(timerInterval.value);
    clearInterval(molePopInterval.value);

    // 清理觸控狀態
    processedTouches.value.clear();

    // 停止所有音效
    stopSound(backgroundMusic.value);
    stopSound(wrongHitSound.value);
    stopSound(gameOverSound.value);

    // 重設分數
    score.value = 0;
    correctCount.value = 0;
    wrongCount.value = 0;
    
    // 重設時間
    timeRemaining.value = gameConfig.gameDuration;
    
    // 重設地鼠洞
    initMoleHoles();
};

// 切換答案彈窗顯示
const toggleAnswerModal = () => {
    showAnswerModal.value = !showAnswerModal.value;
};

// 前往設置頁面
const goToSettings = () => {
    router.push('/ht-molegame-setting');
};

// 🆕 處理 HiTeach 名單選擇
const handleRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        studentInfo.name = '';
        studentInfo.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        studentInfo.name = selectedMember.memberName;
        studentInfo.seatNumber = String(selectedMember.seatID);
        console.log('👤 選擇學生:', selectedMember);
    }
};

// 根據選擇的遊戲模板進行跳轉
const navigateToSelectedGame = () => {
    // 如果選擇的是當前遊戲，不進行跳轉
    if (selectedGameTemplate.value === 'mole') {
        return;
    }

    // 取得 URL 參數
    const currentUrl = new URL(window.location.href);
    const actId = currentUrl.searchParams.get('actId');

    // 在 sessionStorage 標記已切換遊戲（跨頁面保存，並與 actId 綁定）
    if (actId) {
        sessionStorage.setItem('hasGameSwitched', JSON.stringify({
            actId,
            switched: true
        }));
    } else {
        // 非 actId 模式，清除先前的切換標記，改由 URL 的 switched=1 判斷
        sessionStorage.removeItem('hasGameSwitched');
    }

    // 保存當前遊戲資訊到 sessionStorage
    sessionStorage.setItem('switchedFromGame', 'mole');

    // 根據選擇的遊戲模板決定跳轉路徑
    let targetRoute = '';

    switch (selectedGameTemplate.value) {
        case 'star':
            targetRoute = '/ht-pvpgame-play';
            break;
        case 'snake':
            targetRoute = '/ht-snakegame-play';
            break;
        default:
            console.error('未知的遊戲模板:', selectedGameTemplate.value);
            return;
    }

    // 構建完整的跳轉路徑
    if (actId) {
        // actId 模式：保留 actId 參數，並設定 switched=1
        router.push(`${targetRoute}?actId=${actId}&switched=1`);
    } else {
        // 其他模式：直接跳轉並設定 switched=1
        router.push(`${targetRoute}?switched=1`);
    }
};

// 推送到終端（舊版本 HiTeach）
const sendToWebIRS = () => {
    // console.log({action: 'Send.WebIRS', data: {game: 'moleGame', gameConfig: JSON.stringify(gameConfig)}})
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'moleGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 從當前遊戲配置創建 GameActivity
// 已移至 useGameActivityPush composable：
// - createGameActivityFromCurrentConfig
// - generateShortUrl
// - updateActivityWithShortUrl
// - createAndPushShortUrl

// 實際執行推送邏輯
const executePush = async () => {
    console.log('按鈕點擊，當前禁用狀態：', isSendDisabled.value);
    console.log('HiTeach 版本：', htVersion.value, '支援新 Push 功能：', isNewPushFuncVersion.value);

    if (isSendDisabled.value) {
        iviewMessage.warning({
            content: t('Molegame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }

    // 設置冷卻狀態
    isSendDisabled.value = true;
    console.log('設置為禁用狀態：', isSendDisabled.value);

    try {
        if (isNewPushFuncVersion.value) {
            // 新版本 HiTeach (>= 5.5.0.0002)：創建活動 + 推送短網址
            console.log('[HTMoleGamePlay] 使用新版 Push 功能（創建活動 + 短網址）');

            // ✨ 修改：接收返回值
            const result = await createAndPushShortUrl(gameConfig, 0); // Mole 遊戲 playerCount 固定為 0

            // ✨ 新增：更新最新推送的活動 ID（覆蓋舊值）
            if (result.success && result.activityId) {
                pushedActivityId.value = result.activityId;
                pushedActivityName.value = gameConfig.topic || '打地鼠遊戲';
                // ✅ 同步更新 classRosterSource（根據 useHiTeachRoster 判斷）
                classRosterSource.value = gameConfig.useHiTeachRoster ? 'HT' : 'general';
                console.log('[HTMoleGamePlay] 已更新最新推送活動 ID:', result.activityId);
                console.log('[HTMoleGamePlay] 已更新 classRosterSource:', classRosterSource.value);
                console.log('[HTMoleGamePlay] ⚠️ 注意：這會覆蓋之前的 activityId');

                // ✨ 提示使用者可以查看成績
                iviewMessage.info({
                    content: t('HTwbActKitHome.canViewScores') || '推送成功！您可以點擊查看成績按鈕查看學生提交的成績',
                    duration: 5,
                    closable: true
                });
            }
        } else {
            // 舊版本 HiTeach 或無版本資訊：推送完整 JSON（維持現有邏輯）
            console.log('[HTMoleGamePlay] 使用舊版 Push 功能（推送 JSON）');
            sendToWebIRS();
            iviewMessage.success({
                content: t('Molegame.sendSuccess'),
                duration: 3,
                closable: true
            });
            // 注意：舊版本無法取得 activityId，查看成績按鈕不會顯示
        }
    } catch (error) {
        console.error('[HTMoleGamePlay] Push 失敗:', error);
        // 錯誤訊息已在 createAndPushShortUrl 中顯示
    }

    // 1分鐘後恢復
    cooldownTimer.value = setTimeout(() => {
        isSendDisabled.value = false;
        console.log('恢復為啟用狀態：', isSendDisabled.value);
    }, 60000); // 60秒
};

// ✨ 開啟成績查看面板
const openScoresPanel = () => {
    if (!pushedActivityId.value) {
        iviewMessage.warning({
            content: t('HTwbActKitHome.noActivityPushed') || '尚未推送活動，無法查看成績',
            duration: 3,
            closable: true
        });
        return;
    }
    showScoresPanel.value = true;
};

// ✨ 關閉成績查看面板
const closeScoresPanel = () => {
    showScoresPanel.value = false;
};

// 初始化音效
const initSounds = () => {
    // 創建音效對象
    readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
    goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
    backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
    hitSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/punch-41105.mp3'));
    wrongHitSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/cartoon-scream-1-6835.mp3'));
    gameOverSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
    
    // 設置背景音樂循環播放
    backgroundMusic.value.loop = true;
    
    // 預加載音效
    readySound.value.load();
    goSound.value.load();
    backgroundMusic.value.load();
    hitSound.value.load();
    wrongHitSound.value.load();
    gameOverSound.value.load();
};

// 播放音效的方法
const playSound = (sound) => {
    if (isMuted.value || !sound) return;
    
    // 先暫停音效
    sound.pause();
    sound.currentTime = 0;
    
    // 使用 setTimeout 延遲播放，避免播放和暫停操作衝突
    setTimeout(() => {
        sound.play().catch(() => {});
    }, 10);
};

// 停止音效的方法
const stopSound = (sound) => {
    if (!sound) return;

    sound.pause();
    sound.currentTime = 0;
};

// 預覽模式下的頁面重新整理警告
const handleBeforeUnload = (event) => {
    if (isPreviewMode.value) {
        // 現代瀏覽器要求設定 returnValue 為空字串才會顯示確認對話框
        event.preventDefault();
        event.returnValue = ''; // 必須設定為空字串
        return ''; // 相容舊版瀏覽器
    }
};

// 載入預覽模式參數
const loadPreviewMode = (params) => {
    // console.log('🎮 [預覽模式] 收到的完整參數:', params);
    isPreviewMode.value = true;

    // 從 params.gameParameters 提取遊戲參數
    const gp = params.gameParameters || {};
    // console.log('📋 [預覽模式] gameParameters:', gp);
    // console.log('📝 [預覽模式] vocabularyList 數量:', gp.vocabularyList?.length || 0);
    // console.log('❌ [預覽模式] errorVocabularyList 數量:', gp.errorVocabularyList?.length || 0);

    // 映射到 gameConfig 格式
    Object.assign(gameConfig, {
        topic: gp.topic || 'Preview',
        correctVocabularyList: gp.vocabularyList || [],
        errorVocabularyList: gp.errorVocabularyList || [],
        gameDuration: gp.gameDuration || 30,
        background: gp.background || 'forest',
        moleSpeed: gp.moleSpeed || 2500
    });

    // console.log('✅ [預覽模式] 最終 gameConfig:', {
    //     topic: gameConfig.topic,
    //     correctVocabularyList: gameConfig.correctVocabularyList,
    //     errorVocabularyList: gameConfig.errorVocabularyList,
    //     gameDuration: gameConfig.gameDuration,
    //     background: gameConfig.background,
    //     moleSpeed: gameConfig.moleSpeed
    // });

    timeRemaining.value = gameConfig.gameDuration;

    // 顯示預覽模式提示（使用 nextTick 確保 Message 組件已完全初始化）
    nextTick(() => {
        try {
            if (iviewMessage && typeof iviewMessage.info === 'function') {
                iviewMessage.info({
                    content: t('gameActManager.previewRefreshWarning'),
                    duration: 5,
                    closable: true
                });
            } else {
                console.warn('[打地鼠預覽模式] iviewMessage 未正確注入，使用 console 提示');
                console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
            }
        } catch (error) {
            console.error('[打地鼠預覽模式] 顯示提示訊息失敗:', error);
            console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
        }
    });
};

// 初始化 Auth Token
const initializeAuth = async () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_token_expiry");
    try {
        const res = await api.main.getTokenUnlogin();
        const exp = jwt_decode(res.auth_token).exp;
        localStorage.setItem("auth_token", res.auth_token);
        localStorage.setItem("auth_token_expiry", exp);
    } catch (error) {
        console.error("Error fetching auth token", error);
    }
};

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error('載入失敗');
        }

        const config = await response.json();
        Object.assign(gameConfig, JSON.parse(config.data.gameConfig));
        timeRemaining.value = gameConfig.gameDuration;

        iviewMessage.success({
            content: t('Molegame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('Molegame.configLoadFailed'),
            duration: 3,
            closable: true
        });

        // 使用預設配置
        Object.assign(gameConfig, {
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "eraser", "ruler", "scissors", "calculator", "backpack", "paper", "stapler"],
            gameDuration: 30,
            background: "forest",
            moleSpeed: 2500
        });
        timeRemaining.value = gameConfig.gameDuration;
    }
};

// 視窗大小變化處理函數（需要保存引用以便移除）
const handleResize = () => {
    if (gameAreaRef.value) {
        const rect = gameAreaRef.value.getBoundingClientRect();
        gameAreaWidth.value = rect.width;
        gameAreaHeight.value = rect.height;
    }
};

// 組件掛載時
onMounted(async () => {
    // 載入 HiTeach 版本號
    loadVersion();

    // 初始化音效
    initSounds();

    // 初始化遊戲區域尺寸
    if (gameAreaRef.value) {
        const rect = gameAreaRef.value.getBoundingClientRect();
        gameAreaWidth.value = rect.width;
        gameAreaHeight.value = rect.height;
    }

    // 初始化地鼠洞
    initMoleHoles();

    // 監聽視窗大小變化（需要在所有情況下都註冊）
    window.addEventListener('resize', handleResize);

    // 預覽模式下監聽頁面重新整理
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 0. 檢查是否從其他遊戲切換過來
    const urlParams = new URLSearchParams(window.location.search);
    const switched = urlParams.get('switched');

    // 1. 最優先檢查 actId 參數（正式活動模式）
    const actId = urlParams.get('actId');

    // 依據 actId 綁定的切換狀態判斷是否為「此活動」的模板切換
    let hasSwitchedThisActivity = false;
    try {
        const stored = JSON.parse(sessionStorage.getItem('hasGameSwitched') || '{}');
        if (stored && stored.actId && stored.actId === actId && stored.switched) {
            hasSwitchedThisActivity = true;
        }
    } catch (e) {
        hasSwitchedThisActivity = false;
    }

    if (switched === '1' || hasSwitchedThisActivity) {
        hasGameSwitched.value = true;
        console.log('🔄 檢測到遊戲切換，將不會提交成績');
    }
    if (actId) {
        isFromActId.value = true;

        // 先初始化 auth_token（actId 模式需要匿名 token）
        await initializeAuth();

        try {
            // 呼叫公開 API 獲取活動資料
            const response = await gameActivityAPI.getGameActivity({ id: actId });
            console.log('🎮 API Response:', response);
            const activity = response.data;
            console.log('🎯 Activity Data:', activity);

            // 🆕 檢查 currentGame 是否與當前頁面匹配（使用中央化路由驗證）
            if (validateAndRedirectGame(activity, ['mol', 'mole'], router, actId, switched)) {
                return; // 已重導向，中斷後續邏輯
            }

            // 讀取是否允許切換遊戲模板
            allowTemplateChange.value = activity.allowTemplateChange ?? true; // 預設允許
            console.log('🔀 Allow Template Change:', allowTemplateChange.value);

            // 檢查是否為原始遊戲（用於判斷是否提交成績）
            const originalGame = activity.currentGame; // API 回傳的原始遊戲類型：'star' 或 'mole'
            const isOriginalGame = originalGame === 'mole' || originalGame === 'mol'; // 打地鼠頁面：檢查原始遊戲是否為 mole
            console.log('🎮 Original Game:', originalGame, '| Is Original Game:', isOriginalGame);

            // 如果當前是原始遊戲，即使 URL 有 switched=1，也要提交成績
            if (isOriginalGame && hasGameSwitched.value) {
                console.log('✅ 檢測到返回原始遊戲（打地鼠），將恢復成績提交功能');
                hasGameSwitched.value = false; // 重置切換標記
                sessionStorage.removeItem('hasGameSwitched'); // 清除 session 標記
            }

            // 檢查活動是否開放進入
            if (!activity.enableEntry) {
                iviewMessage.warning({
                    content: t('gameActManager.activityNotOpen'),
                    duration: 0,
                    closable: true
                });
                beforeGame.value = false;
                return;
            }

            // 解析 gameContentSettings (JSON string)
            console.log('📝 Raw gameContentSettings:', activity.gameContentSettings);
            const settings = JSON.parse(activity.gameContentSettings);
            console.log('✅ Parsed Settings:', settings);

            // 🆕 解析 HiTeach 名單資料
            classRosterSource.value = activity.classRosterSource || '';
            useHiTeachRoster.value = settings.useHiTeachRoster || false;

            if (classRosterSource.value === 'HT' && activity.classRoster) {
                try {
                    classRoster.value = JSON.parse(activity.classRoster);
                    console.log('👥 HiTeach 名單:', classRoster.value);
                } catch (error) {
                    console.error('❌ 解析 classRoster 失敗:', error);
                    classRoster.value = [];
                }
            }

            // 檢查 settings 是否為空，若為空則使用預設值
            if (Object.keys(settings).length === 0) {
                console.warn('⚠️ gameContentSettings 是空的，使用預設遊戲參數');
                // 打地鼠預設參數
                Object.assign(gameConfig, {
                    topic: activity.activityName || '打地鼠遊戲',
                    correctVocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon'],
                    errorVocabularyList: ['rock', 'bomb', 'stone', 'trap', 'danger'],
                    gameDuration: 60,
                    moleSpeed: 2500,
                    background: 'forest'
                });
            } else {
                // 處理 API 回傳的資料格式差異
                // API 使用 vocabularyList，程式碼使用 correctVocabularyList
                if (settings.vocabularyList && !settings.correctVocabularyList) {
                    settings.correctVocabularyList = settings.vocabularyList;
                }
                // 載入到 gameConfig
                Object.assign(gameConfig, settings);
            }

            console.log('🎲 Final gameConfig:', gameConfig);
            timeRemaining.value = gameConfig.gameDuration;

            iviewMessage.success({
                content: t('gameActManager.activityLoadSuccess'),
                duration: 2,
                closable: true
            });

            // 正式活動模式下直接 return，不執行後續的 WebView/Demo 邏輯
            return;

        } catch (error) {
            console.error('載入活動失敗:', error);
            iviewMessage.error({
                content: t('gameActManager.loadActivityFailed'),
                duration: 3,
                closable: true
            });
            isFromActId.value = false;
            // 載入失敗後繼續執行 Demo 模式
        }
    }

    // 1. 優先檢查預覽模式
    const previewData = localStorage.getItem('game_preview_data');
    const previewTimestamp = localStorage.getItem('game_preview_timestamp');

    // 🆕 調試日誌：檢查 localStorage 是否有資料
    console.log('[打地鼠] localStorage 檢查:', {
        hasPreviewData: !!previewData,
        hasTimestamp: !!previewTimestamp,
        previewDataLength: previewData?.length || 0
    });

    if (previewData && previewTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(previewTimestamp, 10);
        const fiveMinutes = 5 * 60 * 1000;
        const timeElapsed = now - timestamp;

        // 🆕 調試日誌：時效檢查
        console.log('[打地鼠] 時效檢查:', {
            timeElapsed: `${(timeElapsed / 1000).toFixed(1)}秒`,
            isValid: timeElapsed < fiveMinutes,
            threshold: `${fiveMinutes / 1000}秒`
        });

        // 檢查是否在 5 分鐘內
        if (now - timestamp < fiveMinutes) {
            try {
                const params = JSON.parse(previewData);

                // 🆕 調試日誌：解析成功
                console.log('[打地鼠] 預覽參數解析成功:', {
                    currentGame: params.currentGame,
                    playerCount: params.playerCount,
                    hasGameParameters: !!params.gameParameters
                });

                loadPreviewMode(params);

                // 🆕 延遲清除 localStorage（30秒後），避免頁面重新整理時資料遺失
                // 這樣即使使用者重新整理頁面，預覽資料仍然可用
                setTimeout(() => {
                    localStorage.removeItem('game_preview_data');
                    localStorage.removeItem('game_preview_timestamp');
                    console.log('[打地鼠] 預覽資料已清除（30秒後）');
                }, 30000); // 30秒後清除

                // 跳出，不執行後續邏輯
                return;
            } catch (error) {
                console.error('[打地鼠] 預覽參數解析失敗:', error);
                localStorage.removeItem('game_preview_data');
                localStorage.removeItem('game_preview_timestamp');
            }
        } else {
            // 超過時效，清除
            if (process.env.NODE_ENV === 'development') {
                console.log('[打地鼠] 預覽資料已過期，清除');
            }
            localStorage.removeItem('game_preview_data');
            localStorage.removeItem('game_preview_timestamp');
        }
    }

    // 2. 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');

    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
    } else {
        hasConfigUrl.value = false;

        // 3. 發送初始化訊息，並接收遊戲配置（WebView/Demo 模式）
        messageBridge.postMessage({ action: 'Init.MoleGame', data: {
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "eraser", "ruler", "scissors", "calculator", "backpack", "paper", "stapler"],
            gameDuration: 30,
            background: "forest",
            moleSpeed: 2500
        } });

        // 保存監聽器引用以便在 onUnmounted 時移除
        const moleGameMessageHandler = (event) => {
            const msg = event.data;
            console.log('[HTMoleGamePlay] 收到訊息:', msg.action, msg);

            // 監聽 Init.MoleGame 訊息,載入遊戲配置
            // 注意：Init.GameHome 已由 useGameMessageBridge composable 統一處理
            if (msg.action === 'Init.MoleGame') {
                Object.assign(gameConfig, msg.data);
                timeRemaining.value = gameConfig.gameDuration;
                // 將設定存入 sessionStorage
                sessionStorage.setItem('MoleGameConfig', JSON.stringify(msg.data));
            }
        };
        messageBridge.addEventListener(moleGameMessageHandler);

        // 儲存監聽器引用到外部變數
        messageHandlerRef = moleGameMessageHandler;
    }
});

// 組件卸載時
onUnmounted(() => {
    // 清理計時器
    clearInterval(timerInterval.value);
    clearInterval(molePopInterval.value);
    clearTimeout(cooldownTimer.value);

    // 清理觸控狀態
    processedTouches.value.clear();

    // 移除視窗大小變化監聽
    window.removeEventListener('resize', handleResize);

    // 移除頁面重新整理警告監聽
    window.removeEventListener('beforeunload', handleBeforeUnload);

    // 移除 messageBridge 監聽器
    if (messageHandlerRef) {
        messageBridge.removeEventListener(messageHandlerRef);
        messageHandlerRef = null;
    }

    // 停止所有音效
    stopSound(readySound.value);
    stopSound(goSound.value);
    stopSound(backgroundMusic.value);
    stopSound(hitSound.value);
    stopSound(wrongHitSound.value);
    stopSound(gameOverSound.value);
});
</script>

<style lang="less" scoped>
/* 正式活動模式：外層容器（包含彈窗和切換遊戲區塊） */
.actid-mode-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: auto;
  max-width: 90vw;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 正式活動模式：單層左右佈局，寬度自適應 */
.actid-mode-modal {
  display: flex;
  gap: 0;
  width: 100%;
  background: transparent;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

/* 左側：遊戲資訊和播放按鈕 */
.game-info-left {
  min-width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px 24px;
  background: rgba(255, 249, 235, 0.98);
  border-radius: 12px 0 0 12px;

  .game-title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
    padding: 0 12px;
    color: #8B4513;
    text-align: center;
    white-space: nowrap;
  }

  .game-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-size: 14px;
    color: #333;
    width: 100%;
    margin-bottom: 8px;

    .game-time {
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 6px;
      text-align: center;
      white-space: nowrap;
      font-weight: bold;
      font-size: 16px;
      color: #333;
    }
  }

  /* 左側播放按鈕樣式 */
  .play-in-left {
    margin-top: 8px;
  }

  /* 按鈕區：橫向排列 */
  .left-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center;
    margin-top: 8px;

    .play-button,
    .layout-toggle-button {
      margin: 0;
    }
  }
}

/* 右側：學生資訊輸入 */
.student-info-right {
  min-width: 280px;
  padding: 30px 28px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 0 12px 12px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .student-info-title {
    font-size: 17px;
    font-weight: bold;
    margin-bottom: 18px;
    text-align: center;
    color: #333;
  }

  .form-group {
    margin-bottom: 14px;

    .student-input {
      width: 100%;
      padding: 11px 14px;
      font-size: 15px;
      border: 2px solid #ddd;
      border-radius: 8px;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: #4CAF50;
      }

      &::placeholder {
        color: #999;
      }
    }

    .student-select {
      cursor: pointer;
      background-color: white;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;

      &:hover {
        border-color: #4CAF50;
      }
    }

    .error-text {
      display: block;
      margin-top: 6px;
      font-size: 12px;
      color: #ff4d4f;
    }
  }

  // 已選擇資訊顯示區塊
  .selected-info {
    margin-top: 16px;
    padding: 14px;
    background: rgba(76, 175, 80, 0.08);
    border-radius: 8px;
    border: 1px solid rgba(76, 175, 80, 0.3);

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 14px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-label {
        font-weight: bold;
        color: #333;
        margin-right: 8px;
        min-width: 60px;
      }

      .info-value {
        color: #4CAF50;
        font-weight: 600;
      }
    }
  }
}

/* 切換遊戲模板區塊 */
.game-template-switcher {
  width: 100%;
  padding: 20px;
  background: rgba(248, 249, 250, 0.98);
  border-radius: 12px;
  margin-top: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .switcher-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 12px;
    color: #333;
    text-align: center;
  }

  .switcher-content {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;

    .template-select {
      flex: 1;
      max-width: 400px;
      padding: 10px 14px;
      font-size: 15px;
      border: 2px solid #ddd;
      border-radius: 8px;
      background-color: white;
      cursor: pointer;
      transition: border-color 0.2s;

      &:focus {
        outline: none;
        border-color: #FF9800;
      }
    }

    .goto-button {
      width: 48px;
      height: 48px;
      background-color: #FF9800;
      border: none;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background-color: #F57C00;
        transform: scale(1.05);
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        opacity: 0.6;
      }

      svg {
        width: 24px;
        height: 24px;
        color: white;
      }
    }
  }
}

/* 遊戲模板切換按鈕（在播放按鈕旁邊） - 已移除，保留舊樣式以防其他地方使用 */
.layout-toggle-button {
  width: 60px;
  height: 60px;
  background-color: #FF9800;
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #FB8C00;
  }

  .change-icon {
    width: 28px;
    height: 28px;
    color: white;

    ::v-deep svg {
      fill: white !important;

      * {
        fill: white !important;
      }
    }
  }
}

@import '../assets/HTwbActKitCSS/HTMoleGame.less';
</style>